import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Img } from 'react-image';
import Dialog from '../layout/Dialog';
import Panel from '../layout/Panel';
import { LayoutOrientations, DndItemTypes, AssetTypes } from '../../globals';
import AssetProperty from '../properties/AssetProperty';
import BoolProperty from '../properties/BoolProperty';
import Spacer from '../layout/Spacer';
import AssetUtility from '../../classes/AssetUtility';
import Engine from '../../classes/Engine';
import ListView from '../listComponent/ListView';
import ListItem from '../listComponent/ListItem';
import { Spinner } from '../Iconset';
import PlaceholderContent from '../layout/PlaceholderContent';
import ReplaceModelAsset from '../../actions/assetSwap/ReplaceModelAsset';
import { assert } from '../../classes/utilities/ErrorHandling';
import AssetsManager from '../../classes/AssetsManager';
import useForceUpdate from '../reactUtils/useForceUpdate';
import StandbyOverlay from '../layout/StandbyOverlay';

enum NodeStatuses {
	DELETED,
	ADDED,
	MODIFIED,
	REMAPPED,
}

export default function AssetSwapDialog(props)
{
	const forceUpdate = useForceUpdate();

	const assetProps = props.sourceAssetProps;
	assert(assetProps, "missing source asset props for asset swap");

	const type = assetProps.type === AssetTypes.ModelProcessed || assetProps.type === AssetTypes.RevisionProcessed ? AssetTypes.Model : assetProps.type;
	const [oldModel, setOldModel] = useState(Engine.getInstance().getModelAsset(assetProps.nodeId)!);

	const [newAssetId, setNewAssetId] = useState<string>("");
	const [newModel, setNewModel] = useState<THREE.Object3D | null>(null);

	const [selectedOldItem, setSelectedOldItem] = useState(null);
	const [selectedNewItem, setSelectedNewItem] = useState(null);

	const [keepOriginalMaterials, setkeepOriginalMaterials] = useState(false);

	const sourceInfoMap = useRef(new Map());
	const targetInfoMap = useRef(new Map());


	useEffect(() =>
	{
		setOldModel(Engine.getInstance().getModelAsset(props.sourceAssetProps.nodeId)!);
	}, [props.sourceAssetProps]);

	useEffect(() =>
	{
		if (newAssetId && type === AssetTypes.Model)
		{
			let newModel = Engine.getInstance().getModelAsset(newAssetId);
			const modelDownloadPromise = new Array<any>();
			if (!newModel)
			{
				StandbyOverlay.startStandbyMode();
				const url = AssetUtility.getProcessedAssetURL(newAssetId);
				const assetName = AssetUtility.getAssetName(newAssetId)!;
				modelDownloadPromise.push(Engine.getInstance().prepareModel(newAssetId, url, assetName, undefined, false, true));
			}

			Promise.all(modelDownloadPromise).then(function ()
			{
				newModel = Engine.getInstance().getModelAsset(newAssetId)!;
				setNewModel(newModel);
				if(modelDownloadPromise.length) StandbyOverlay.stopStandbyMode();
			});
		}
	}, [newAssetId]);

	useEffect(() =>
	{
		if (newModel)
		{
			sourceInfoMap.current.clear();
			targetInfoMap.current.clear();
			populateInfoMaps(oldModel, newModel);

			sourceInfoMap.current.set(oldModel.userData.guid, {
				object: oldModel,
				status: NodeStatuses.REMAPPED,
				target: newModel.userData.guid,
			});

			targetInfoMap.current.set(newModel.userData.guid, {
				object: newModel,
				status: NodeStatuses.REMAPPED,
				target: oldModel.userData.guid,
				parent: null,
			});

			// go over B and mark all unassigned as ADDED
			newModel.traverse(child =>
			{
				if (!child.userData.hideInHierarchy && !targetInfoMap.current.has(child.userData.guid))
				{
					// set to parent itself
					targetInfoMap.current.set(child.userData.guid, {
						object: child,
						status: NodeStatuses.ADDED,
						parent: child.parent,
					});
				}
			});

			forceUpdate();
		}
	}, [newModel]);

	function populateInfoMaps(modelA, modelB)
	{
		for (let cA = 0; cA < modelA.children.length; cA++)
		{
			const childA = modelA.children[cA];
			if (childA.userData.hideInHierarchy) continue;

			// start with status = deleted
			sourceInfoMap.current.set(childA.userData.guid, { object: childA, status: NodeStatuses.DELETED });

			let foundParallelNode = false;

			// find child with same name on B
			for (let cB = 0; cB < modelB.children.length; cB++)
			{
				const childB = modelB.children[cB];
				if (childB.userData.hideInHierarchy) continue;

				//skip assigned
				if (targetInfoMap.current.has(childB.userData.guid)) continue;

				// found, assign, status = REMAPPED
				if (childA.name === childB.name)
				{
					let status = NodeStatuses.REMAPPED;

					// check for mesh differences and if so, mark as MODIFIED
					if(childA instanceof THREE.Mesh && childB instanceof THREE.Mesh)
					{
						// compare vertex / triangle count
						if(childA.geometry.attributes.position.count !== childB.geometry.attributes.position.count ||
							childA.geometry.index.count !== childB.geometry.index.count)
						{
							status = NodeStatuses.MODIFIED;
						}
					}

					// set up the remap link between A and B
					sourceInfoMap.current.set(childA.userData.guid, {
						...sourceInfoMap.current.get(childA.userData.guid),
						status: status,
						target: childB.userData.guid,
					});

					// mark B as remapped
					targetInfoMap.current.set(childB.userData.guid, {
						object: childB,
						status: status,
						target: childA.userData.guid,
						parent: childB.parent,
					});

					// dive deeper to compare sub-hierarchy
					populateInfoMaps(childA, childB);

					foundParallelNode = true;
					break;
				}
			}

			if (!foundParallelNode)
			{
				populateInfoMaps(childA, modelB);
			}
		}
	}

	function renderAssetPreview(assetId)
	{
		const assetData = AssetsManager.getAssetInfo(assetId); // TODO: replace with AssetsManager.getAssetInfo
		if (assetData)
		{
			switch (assetData.type)
			{
				case AssetTypes.Model:
				case AssetTypes.RevisionProcessed:
				case AssetTypes.ModelProcessed:
				case AssetTypes.Image:
					return (
						<div className={"assetPreviewImage centerContent"}
							style={{
								width: "75px",
								height: "75px",
							}}
						>
							<Img className={"assetSwapPreview"}
								src={AssetUtility.getPreviewAssetURL(assetData.id)}
								loader={<Spinner size={70} />}
								width={75}
								height={75}
							/>
						</div>
					);
				default:
					return null;
			}
		}
	}

	function onOldSelectionChanged(nodePropsSet)
	{
		const nodeProps = nodePropsSet.values().next().value;
		if (!nodeProps)
		{
			setSelectedOldItem(null);
			return;
		}

		setSelectedOldItem(nodeProps.nodeId);
	}

	function onNewSelectionChanged(nodePropsSet)
	{
		const nodeProps = nodePropsSet.values().next().value;
		if (!nodeProps)
		{
			setSelectedNewItem(null);
			return;
		}

		setSelectedNewItem(nodeProps.nodeId);
	}

	function getStatusIcon(object)
	{
		const info = sourceInfoMap.current.get(object.userData.guid) ?? targetInfoMap.current.get(object.userData.guid);
		if (!info) return;

		return <div className={"defaultConnection"} style={{ backgroundColor: nodeColorsByStatus[info.status] }} />;
	}

	const nodeColorsByStatus =
	{
		[NodeStatuses.ADDED]: "#0f0",
		[NodeStatuses.DELETED]: "#ff9a71", // red color shade according to AA contrast standards: https://www.learnui.design/tools/accessible-color-generator.html using #464646 as background color
		[NodeStatuses.REMAPPED]: "",
		[NodeStatuses.MODIFIED]: "#0ff",
	};

	function getStatus(object: THREE.Object3D)
	{
		const info = sourceInfoMap.current.get(object.userData.guid) ?? targetInfoMap.current.get(object.userData.guid);
		if (!info) return;

		let label = NodeStatuses[info.status];
		if (info.status === NodeStatuses.REMAPPED)
		{
			const targetInfo = sourceInfoMap.current.get(info.target) ?? targetInfoMap.current.get(info.target);
			if( targetInfo.object.name !== object.name)
			{
				label += ": " + targetInfo.object.name;
			}
		}

		const nodeColor = nodeColorsByStatus[info.status];

		return (
			<div className={"visibilityLabel"}
				style={{
					color: nodeColor,
				}}
			>
				{label}
			</div>
		);
	}

	function renderChildrenOfObject(object: THREE.Object3D)
	{
		if (!object)
			return [];

		const renderObjects = new Array<JSX.Element>();
		for (let index = 0; index < object.children.length; index++)
		{
			const child = object.children[index];
			if (child.userData.hideInHierarchy)
				continue;

			renderObjects.push(
				<ListItem
					key={child.userData.guid}
					nodeId={child.userData.guid}
					label={child.name}
					parent={object}
					iconRenderer={newModel && getStatusIcon(child)}
					decorators={newModel && getStatus(child)}
				>
					{
						renderChildrenOfObject(child)
					}
				</ListItem>
			);
		}

		return renderObjects;
	}

	function onNodeDroppedForRemap(draggedNodesProps, dropTargetProps)
	{
		if (!dropTargetProps)
		{
			// reset previous target
			const sourceInfo = sourceInfoMap.current.get(draggedNodesProps[0].nodeId);
			if (sourceInfo.target)
			{
				targetInfoMap.current.set(sourceInfo.target, {
					...targetInfoMap.current.get(sourceInfo.target),
					target: null,
					status: NodeStatuses.ADDED,
				});
			}

			// reset dropped
			sourceInfoMap.current.set(draggedNodesProps[0].nodeId, {
				...sourceInfoMap.current.get(draggedNodesProps[0].nodeId),
				status: NodeStatuses.DELETED,
				target: null,
			});

			forceUpdate();
			return;
		}

		// console.log(draggedNodesProps);
		// console.log(dropTargetProps);

		const sourceInfo = sourceInfoMap.current.get(draggedNodesProps[0].nodeId);
		const targetInfo = targetInfoMap.current.get(dropTargetProps.nodeId);

		// reset previous target
		if (sourceInfo.target)
		{
			targetInfoMap.current.set(sourceInfo.target, {
				...targetInfoMap.current.get(sourceInfo.target),
				target: null,
				status: NodeStatuses.ADDED,
			});
		}

		// reset previous source
		if (targetInfo.target)
		{
			sourceInfoMap.current.set(targetInfo.target, {
				...sourceInfoMap.current.get(targetInfo.target),
				target: null,
				status: NodeStatuses.DELETED,
			});
		}

		// remap from dropped node to target node
		sourceInfoMap.current.set(draggedNodesProps[0].nodeId, {
			...sourceInfoMap.current.get(draggedNodesProps[0].nodeId),
			status: NodeStatuses.REMAPPED,
			target: dropTargetProps.nodeId,
		});

		// set target remapped
		targetInfoMap.current.set(dropTargetProps.nodeId, {
			...targetInfoMap.current.get(dropTargetProps.nodeId),
			status: NodeStatuses.REMAPPED,
			target: draggedNodesProps[0].nodeId,
		});

		// console.log(sourceInfoMap);
		// console.log(targetInfoMap);

		forceUpdate();
	}

	function submitAssetSwap()
	{
		if (!newAssetId)
			return;

		if (type !== AssetTypes.Model)
		{
			AssetsManager.replaceAssetReferences(assetProps.nodeId, newAssetId);
		}
		else
		{
			if (!newModel)
				return;

			const oldNodesToNew = {};

			for (const [key, value] of sourceInfoMap.current)
			{
				if (value.status === NodeStatuses.DELETED || value.status === NodeStatuses.ADDED) continue;

				const modelData = value.object.userData.modelData;
				assert(modelData);

				const targetModelData = targetInfoMap.current.get(value.target).object.userData.modelData;
				assert(targetModelData);

				if(!oldNodesToNew[modelData.nodeIndex]) oldNodesToNew[modelData.nodeIndex] = {};
				oldNodesToNew[modelData.nodeIndex][modelData.primitiveIndex] = {
					nodeIndex: targetModelData.nodeIndex,
					primitiveIndex: targetModelData.primitiveIndex
				};
			}

			new ReplaceModelAsset(assetProps.nodeId, newAssetId, oldNodesToNew, keepOriginalMaterials);			
		}

		AssetsManager.rebuildUsedAssets();
	}

	/*
		example data structure:

			MODEL A       ||    MODEL B      ||    STATUS          ||    TARGET
		------------------||-----------------||--------------------||-----------------------
		1] hello          || 1] hello        ||    A: MAP, B: MAP  ||    A-1 >> B-1 (can use userData.guid instead)
		   1.1] world     || 2] world        ||    A: DEL, B: NEW  ||    NULL
		2] omega          || 3] omega        ||    A: MAP, B: MAP  ||    A-2 >> B-3
		   2.1] lul       ||    3.1] lul     ||    A: MAP, B: MAP  ||    A-2.1 >> B-3.1
		   2.2] ayaya     ||    3.2] nayaya  ||    A: MAP, B: MAP  ||    A-2.2 >> B-3.4
		   2.3] nayaya    ||    3.3] pickles ||    A: MAP, B: NEW  ||    A-2.3 >> B-3.2
		   2.4] mustard   ||    3.4] ayaya   ||    A: DEL, B: MAP  ||    NULL

		-- oldNodesToNew should be populated like:
		oldNodesToNew[old omega node index][old omega primitive index] = { nodeIndex: new omega node index, primitiveIndex: new omega primitive index };

		-- parentToAddedNodes should be populated like:
		parentToAddedNodes[omega node index][omega primitive index] = []; //unless already exists :ayaya:
		parentToAddedNodes[omega node index][omega primitive index].push{nodeIndex : pickles node index, primitiveIndex : pickles primitive index};
	*/

	function isModel()
	{
		return assetProps.type === AssetTypes.Model ||
		assetProps.type === AssetTypes.ModelProcessed ||
		assetProps.type === AssetTypes.Revision ||
		assetProps.type === AssetTypes.RevisionProcessed;
	}

	return (
		<Dialog
			title={"Replace Project Asset"}
			className={"assetSwapDialogRoot"}
			data-name={"asset-swap-dialog-root"}
			actions={[
				{
					label: "Confirm",
					onClick: submitAssetSwap,
					disabled: !newAssetId || (type === AssetTypes.Model && !newModel),
				},
				{
					label: "Cancel",
				}
			]}
			onShutdown={props.onShutdown}
		>
			<div style={{ overflow: "hidden" }}>
				{
					!isModel() ? null :
						<BoolProperty data-name={"property-replace-materials"}
							label={"Overwrite existing step materials with New Asset materials"}
							value={!keepOriginalMaterials}
							labelClass={"assetSwapOverwriteWideLabel"}
							defaultValue={true}
							onChangeCallback={(val)=> {setkeepOriginalMaterials(!val)}}
						/>
				}
				<Panel className={"assetSwapContentRoot"}
					data-name={"asset-swap-content-root"}
					orientation={LayoutOrientations.Horizontal}
				>
					<Panel className={"assetSwapEntry solid"}
						data-name={"asset-swap-entry-old-asset"}
					>
						<div className={"largeText"}>
							Old Asset:
						</div>
						<Spacer size={{ y: "2px" }} />
						<div className={"assetSwapEntryNameWrapper"}
							data-name={"asset-swap-entry-old-asset-name-wrapper"}
						>
							<div className={"assetSwapEntryName"}
								data-name={"asset-swap-entry-old-asset-name"}>
								{assetProps.label}
							</div>
						</div>
						<Spacer size={{ y: "4px" }} />
						<div className={"assetSwapEntryPreviewContainer"}
							data-name={"asset-swap-entry-old-asset-preview-container"}
						>
							{
								renderAssetPreview(assetProps.nodeId)
							}
						</div>
						{
							type !== AssetTypes.Model ? null :
								<Panel className={"hierarchyMapperRoot solid"}
									data-name={"asset-swap-entry-old-hierarchy-mapper-root"}
								>
									<Spacer size={{ y: "4px" }} />
									<div>
										Drag node to remap
									</div>
									<Spacer size={{ y: "4px" }} />

									<ListView
										selectedItems={new Set().add(selectedOldItem)}
										expandedItems={new Set().add(oldModel.userData.guid)}
										startExpanded={true}
										//selectionChangedCallback={onOldSelectionChanged}
										multiSelectEnabled={false}
										dndDefaults={{
											dragEnabled: true,
											dropEnabled: false,
											dragType: DndItemTypes.ASSET_SWAP_NODE,
										}}
									>
										<ListItem
											key={oldModel.userData.guid}
											nodeId={oldModel.userData.guid}
											isSelectable={false}
											label={oldModel.name}
											parent={null}
											iconRenderer={null}
											decorators={null}
											dndOverrides={{
												dragEnabled: false,
											}}
										>
											{
												renderChildrenOfObject(oldModel)
											}
										</ListItem>
									</ListView>
								</Panel>
						}
					</Panel>
					<Spacer size={{ x: "4px" }} />
					<Panel className={"assetSwapEntry solid composite"}
						data-name={"asset-swap-entry-new-asset"}
					>
						<div className={"largeText"}>
							New Asset:
						</div>
						<Spacer size={{ y: "2px" }} />
						<AssetProperty
							data-name={"asset-swap-entry-old-asset-picker"}
							value={newAssetId}
							onChangeCallback={(v) =>
							{
								if(v === assetProps.nodeId) return;
								setNewAssetId(v);
								setNewModel(null);

								// since the process can be cancelled, we'll add to assets manager on confirm instead of here
								AssetsManager.removeUsedAsset(v);
							}}
							type={type}
							showRevisions={true}
							// filtered={new Set().add(assetProps.nodeId)}
						/>
						<Spacer size={{ y: "4px" }} />
						<div className={"assetSwapEntryPreviewContainer"}
							data-name={"asset-swap-entry-new-asset-preview-container"}
						>
							{
								renderAssetPreview(newAssetId)
							}
						</div>						
						{
							type !== AssetTypes.Model ? null :
								!newAssetId ? null :
									!newModel ?
										<Panel>
											<PlaceholderContent>
												<Spinner size={80} />
												<Spacer size={{ y: "8px" }} />
												Fetching model hierarchy...
											</PlaceholderContent>
										</Panel>
										:
										<Panel className={"hierarchyMapperRoot solid composite"}
											data-name={"asset-swap-entry-new-hierarchy-mapper-root"}
										>
											<Spacer size={{ y: "4px" }} />
											<div>
												Drop node to remap (drop on the root to remove)
											</div>
											<Spacer size={{ y: "4px" }} />
											<ListView
												selectedItems={new Set().add(selectedNewItem)}
												expandedItems={new Set().add(newModel.userData.guid)}
												startExpanded={true}
												//selectionChangedCallback={onNewSelectionChanged}
												itemDroppedCallback={onNodeDroppedForRemap}
												multiSelectEnabled={false}
												dndDefaults={{
													dragEnabled: false,
													dropEnabled: true,
													dropDefinitions: [
														{
															type: DndItemTypes.ASSET_SWAP_NODE,
															callback: onNodeDroppedForRemap
														}
													],
												}}
											>
												<ListItem
													key={newModel.userData.guid}
													nodeId={newModel.userData.guid}
													isSelectable={false}
													label={newModel.name}
													parent={null}
													iconRenderer={null}
													decorators={null}
													dndOverrides={{
														dropEnabled: false,
													}}
												>
													{
														renderChildrenOfObject(newModel)
													}
												</ListItem>
											</ListView>
										</Panel>
						}
					</Panel>
				</Panel>
				{
					type !== AssetTypes.VisageTemplate ? null :
						<p className={"warningText"}>
							Swapping templates will discard any changes you've made to the current template
						</p>
				}
			</div>
		</Dialog>
	);
}
